/**
 * Versammlungs-Detail-Editor (Phase 5).
 * Route: /weg/versammlung/:assemblyId
 *
 * Tabs: Übersicht · Tagesordnung · Präsenz · Protokoll
 * Aktionen: Einladen · Schließen (angenommene TOPs → Beschluss-Sammlung)
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  ArrowLeft, Plus, Trash, PaperPlaneTilt, CheckCircle, Warning, Users, Gavel, ClipboardText, Buildings, IdentificationCard, FileArrowUp, Download,
} from "@phosphor-icons/react";
import { PresentationChart } from "@phosphor-icons/react";
import { openAuthenticatedPdf } from "@/lib/pdfUtils";

const VOTE_KEY_LABEL = { head: "Kopf", mea: "MEA", object: "Objekt" };
const MAJORITY_LABEL = { simple: "einfach", qualified: "qualifiziert (≥75 %)", unanimous: "allstimmig" };
const ATTENDANCE_LABEL = { present: "Anwesend", represented: "Vertreten", absent: "Abwesend" };
const OUTCOME_META = {
  angenommen: { label: "Angenommen", color: "bg-emerald-100 text-emerald-800" },
  abgelehnt: { label: "Abgelehnt", color: "bg-rose-100 text-rose-800" },
  vertagt: { label: "Vertagt (Gleichstand)", color: "bg-amber-100 text-amber-800" },
};
const contactName = (c) => c ? ([c.first_name, c.last_name].filter(Boolean).join(" ") || c.company || "—") : "—";

const TABS = [
  { key: "overview", label: "Übersicht", icon: Buildings },
  { key: "agenda", label: "Tagesordnung", icon: Gavel },
  { key: "attendance", label: "Präsenz", icon: Users },
  { key: "proxies", label: "Vollmachten", icon: IdentificationCard },
  { key: "minutes", label: "Protokoll", icon: ClipboardText },
];

export default function AssemblyDetail() {
  const { assemblyId } = useParams();
  const navigate = useNavigate();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [tab, setTab] = useState("overview");
  const [dirty, setDirty] = useState(false);

  const load = () => api.get(`/weg/assemblies/${assemblyId}`).then((r) => { setA(r.data); setDirty(false); });
  useEffect(() => { load(); }, [assemblyId]); // eslint-disable-line

  const isEditable = a && a.status !== "closed" && a.status !== "cancelled";

  const patch = (updates) => { setA({ ...a, ...updates }); setDirty(true); };
  const setAgenda = (agenda_items) => patch({ agenda_items });
  const setAttendance = (attendance) => patch({ attendance });

  const save = async () => {
    try {
      await api.patch(`/weg/assemblies/${assemblyId}`, {
        property_id: a.property_id,
        title: a.title,
        assembly_date: a.assembly_date,
        location: a.location,
        start_time: a.start_time,
        end_time: a.end_time,
        invited_at: a.invited_at,
        invitation_days_required: Number(a.invitation_days_required) || 21,
        is_reconvened: !!a.is_reconvened,
        quorum_required_pct: Number(a.quorum_required_pct) || 50,
        status: a.status,
        agenda_items: (a.agenda_items || []).map((i) => ({
          number: i.number, title: i.title, description: i.description || null,
          proposal_text: i.proposal_text || null,
          vote_key: i.vote_key || "mea",
          majority_type: i.majority_type || "simple",
          votes_yes: Number(i.votes_yes) || 0,
          votes_no: Number(i.votes_no) || 0,
          votes_abstain: Number(i.votes_abstain) || 0,
          notes: i.notes || null,
          resolution_id: i.resolution_id || null,
        })),
        attendance: (a.attendance || []).map((att) => ({
          contact_id: att.contact_id,
          status: att.status || "absent",
          represented_by_contact_id: att.represented_by_contact_id || null,
          proxy_note: att.proxy_note || null,
        })),
        minutes: a.minutes || null,
        notes: a.notes || null,
      });
      toast.success("Gespeichert");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const invite = async () => {
    if (dirty) await save();
    const r = await api.post(`/weg/assemblies/${assemblyId}/invite`);
    if (r.data.invitation_deadline_met) {
      toast.success(`Einladung markiert. Frist gewahrt (${r.data.days_until_assembly} Tage).`);
    } else {
      toast.warning(`Einladung markiert, aber Frist unterschritten (${r.data.days_until_assembly}/${r.data.required_days} Tage).`);
    }
    load();
  };

  const closeAssembly = async () => {
    if (dirty) await save();
    try {
      const r = await api.post(`/weg/assemblies/${assemblyId}/close`);
      toast.success(`Abgeschlossen. ${r.data.resolutions_created} Beschluss/Beschlüsse übernommen.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  // 2026-08 — Einladungen per E-Mail versenden + Dokumentenmappe archivieren
  const sendInvitations = async () => {
    if (dirty) await save();
    if (!await window._domivusConfirm("Einladungs-PDFs für alle Eigentümer erzeugen und per E-Mail versenden?")) return;
    try {
      const { data } = await api.post(`/weg/assemblies/${assemblyId}/send-invitations`);
      const failed = (data.failed || []).length;
      failed
        ? toast.warning(`${data.sent}/${data.total_owners} versendet · ${failed} fehlgeschlagen (siehe Details)`)
        : toast.success(`${data.sent}/${data.total_owners} Einladungen versendet und archiviert.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Versand");
    }
  };

  // 2026-08 — Protokoll-PDF erzeugen und in Dokumentenmappe archivieren
  const archiveMinutes = async () => {
    if (dirty) await save();
    try {
      const { data } = await api.post(`/weg/assemblies/${assemblyId}/archive-minutes`);
      toast.success(`Protokoll archiviert (${data.filename})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  if (!a) return <div className="p-6 text-sm text-slate-500">Lade …</div>;

  return (
    <div className="space-y-6" data-testid="assembly-detail-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs">
            <Link to={`/objekte/${a.property_id}?tab=weg`} className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
              <ArrowLeft size={12} /> Zurück zum Objekt
            </Link>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mt-1">
            <span className="font-mono text-sm text-slate-500 mr-2">Nr. {a.assembly_number}</span>
            {a.title}
          </h1>
          <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
            <Badge className={`text-[10px] ${a.status === "closed" ? "bg-emerald-100 text-emerald-800" : a.status === "invited" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
              {a.status === "planned" ? "Geplant" : a.status === "invited" ? "Eingeladen" : a.status === "held" ? "Durchgeführt" : a.status === "closed" ? "Abgeschlossen" : "Abgesagt"}
            </Badge>
            <span>{a.assembly_date}</span>
            {a.start_time && <span>· {a.start_time}</span>}
            {a.location && <span>· {a.location}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditable && dirty && (
            <Button size="sm" variant="outline" onClick={save} data-testid="assembly-save-btn">Speichern</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => navigate(`/weg/versammlung/${assemblyId}/praesentation`)} data-testid="assembly-present-btn">
            <PresentationChart size={13} className="mr-1.5" /> Präsentation starten
          </Button>
          {a.status === "planned" && (
            <Button size="sm" onClick={invite} data-testid="assembly-invite-btn">
              <PaperPlaneTilt size={13} className="mr-1.5" /> Einladen (Status)
            </Button>
          )}
          {(a.status === "planned" || a.status === "invited") && (
            <Button size="sm" variant="outline" onClick={sendInvitations} data-testid="assembly-send-invitations-btn" title="Einladungs-PDFs an alle Eigentümer per E-Mail versenden und in Dokumentenmappe archivieren">
              <PaperPlaneTilt size={13} className="mr-1.5" /> Einladungen versenden
            </Button>
          )}
          {a.status !== "planned" && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/weg/pdf/assembly/${assemblyId}/invitation`} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" data-testid="assembly-pdf-invitation">Einladung PDF</Button>
            </a>
          )}
          {(a.status === "held" || a.status === "closed") && (
            <>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/weg/pdf/assembly/${assemblyId}/minutes`} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" data-testid="assembly-pdf-minutes">Protokoll PDF</Button>
              </a>
              <Button size="sm" variant="outline" onClick={archiveMinutes} data-testid="assembly-archive-minutes-btn" title="Protokoll in Dokumentenmappe archivieren">
                Protokoll archivieren
              </Button>
            </>
          )}
          {(a.status === "invited" || a.status === "held") && (
            <Button size="sm" onClick={closeAssembly} data-testid="assembly-close-btn">
              <CheckCircle size={13} className="mr-1.5" /> Abschließen
            </Button>
          )}
        </div>
      </div>

      {a.invited_at && a.invitation_deadline_met === false && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
          <Warning size={13} weight="fill" className="inline mr-1.5" />
          Ladungsfrist unterschritten: nur {a.invitation_days} Tage zwischen Einladung und Versammlung (mind. {a.invitation_days_required ?? 21} Tage erforderlich). Beschlüsse können anfechtbar sein.
        </div>
      )}

      {/* Quorum-Kachel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className={`rounded-lg border p-4 ${a.quorum?.is_quorate ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1">Beschlussfähigkeit</div>
          <div className="text-2xl font-semibold" data-testid="assembly-quorum-pct">{a.quorum?.quorum_pct}%</div>
          <div className="text-xs mt-1">{a.quorum?.reason} — {a.quorum?.counted_mea}/{a.quorum?.total_mea} MEA</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1">Anwesend/Vertreten</div>
          <div className="text-2xl font-semibold">{a.quorum?.counted_owners}/{a.quorum?.total_owners}</div>
          <div className="text-xs text-slate-500 mt-1">Eigentümer</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1">TOPs</div>
          <div className="text-2xl font-semibold">{a.agenda_items?.length || 0}</div>
          <div className="text-xs text-slate-500 mt-1">Beschlüsse: {(a.agenda_items || []).filter((i) => i.resolution_id).length}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`assembly-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 py-2 px-1 text-sm border-b-2 transition ${tab === t.key ? "border-emerald-600 text-emerald-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <OverviewTab a={a} isEditable={isEditable} patch={patch} />
      )}
      {tab === "agenda" && (
        <AgendaTab a={a} isEditable={isEditable} setAgenda={setAgenda} />
      )}
      {tab === "attendance" && (
        <AttendanceTab a={a} isEditable={isEditable} setAttendance={setAttendance} />
      )}
      {tab === "proxies" && (
        <ProxiesTab assemblyId={assemblyId} />
      )}
      {tab === "minutes" && (
        <MinutesTab a={a} isEditable={isEditable} patch={patch} />
      )}
    </div>
  );
}

function OverviewTab({ a, isEditable, patch }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <label className="text-xs text-slate-600 block">Titel
        <Input value={a.title} onChange={(e) => patch({ title: e.target.value })} disabled={!isEditable} />
      </label>
      <label className="text-xs text-slate-600 block">Datum
        <DatePicker value={a.assembly_date} onChange={(v) => patch({ assembly_date: v })} disabled={!isEditable} data-testid="assembly-date" />
      </label>
      <label className="text-xs text-slate-600 block">Ort
        <Input value={a.location || ""} onChange={(e) => patch({ location: e.target.value })} disabled={!isEditable} />
      </label>
      <label className="text-xs text-slate-600 block">Beginn
        <Input type="time" value={a.start_time || ""} onChange={(e) => patch({ start_time: e.target.value })} disabled={!isEditable} />
      </label>
      <label className="text-xs text-slate-600 block">Quorum (%)
        <Input type="number" min="0" max="100" value={a.quorum_required_pct} onChange={(e) => patch({ quorum_required_pct: e.target.value })} disabled={!isEditable} />
      </label>
      <label className="text-xs text-slate-600 block">Ladungsfrist (Tage)
        <Input type="number" min="0" max="90" value={a.invitation_days_required ?? 21}
               onChange={(e) => patch({ invitation_days_required: Number(e.target.value) })}
               disabled={!isEditable} data-testid="assembly-invitation-days"
               title="§ 24 Abs. 4 WEG: Standard 21 Tage. Kann für SEG oder gesonderte Vereinbarungen angepasst werden." />
      </label>
      <label className="text-xs text-slate-700 flex items-center gap-2 pt-6">
        <input type="checkbox" checked={!!a.is_reconvened} onChange={(e) => patch({ is_reconvened: e.target.checked })} disabled={!isEditable} data-testid="assembly-reconvened" />
        Wiederholungsversammlung (immer beschlussfähig)
      </label>
    </div>
  );
}

function AgendaTab({ a, isEditable, setAgenda }) {
  const items = a.agenda_items || [];
  const add = () => setAgenda([...items, {
    number: `TOP ${items.length + 1}`, title: "", vote_key: "mea", majority_type: "simple",
    votes_yes: 0, votes_no: 0, votes_abstain: 0,
  }]);
  const remove = (idx) => setAgenda(items.filter((_, i) => i !== idx));
  const upd = (idx, field, value) => {
    const next = [...items]; next[idx] = { ...next[idx], [field]: value };
    setAgenda(next);
  };
  return (
    <div className="space-y-3">
      {isEditable && (
        <Button size="sm" variant="outline" onClick={add} data-testid="agenda-add-btn"><Plus size={12} className="mr-1" />TOP hinzufügen</Button>
      )}
      {items.map((it, idx) => {
        const outcome = it.outcome ? OUTCOME_META[it.outcome] : null;
        return (
          <div key={idx} className={`rounded-lg border p-3 ${it.resolution_id ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-white"}`} data-testid={`agenda-item-${idx}`}>
            <div className="grid grid-cols-12 gap-2 items-start">
              <Input className="col-span-2 h-8 text-xs" value={it.number} onChange={(e) => upd(idx, "number", e.target.value)} disabled={!isEditable} placeholder="TOP 1" />
              <Input className="col-span-5 h-8 text-xs" value={it.title} onChange={(e) => upd(idx, "title", e.target.value)} disabled={!isEditable} placeholder="Titel" data-testid={`agenda-title-${idx}`} />
              <Select value={it.vote_key || "mea"} onValueChange={(v) => upd(idx, "vote_key", v)} disabled={!isEditable}>
                <SelectTrigger className="col-span-2 h-8 text-xs" title="Verteilungsschlüssel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(VOTE_KEY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={it.majority_type || "simple"} onValueChange={(v) => upd(idx, "majority_type", v)} disabled={!isEditable}>
                <SelectTrigger className="col-span-2 h-8 text-xs" data-testid={`agenda-majority-${idx}`} title="Mehrheit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MAJORITY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              {isEditable && (
                <Button variant="ghost" size="sm" onClick={() => remove(idx)} className="col-span-1"><Trash size={13} className="text-rose-600" /></Button>
              )}
            </div>
            <Textarea className="mt-2 text-xs" value={it.proposal_text || ""} onChange={(e) => upd(idx, "proposal_text", e.target.value)} disabled={!isEditable} rows={2} placeholder="Wortlaut des Beschlussantrags" />
            <div className="mt-2 grid grid-cols-4 gap-2 items-center">
              <label className="text-[11px] text-slate-500">Ja
                <Input type="number" min="0" className="h-7 text-xs" value={it.votes_yes || 0} onChange={(e) => upd(idx, "votes_yes", Number(e.target.value))} disabled={!isEditable} data-testid={`agenda-yes-${idx}`} />
              </label>
              <label className="text-[11px] text-slate-500">Nein
                <Input type="number" min="0" className="h-7 text-xs" value={it.votes_no || 0} onChange={(e) => upd(idx, "votes_no", Number(e.target.value))} disabled={!isEditable} data-testid={`agenda-no-${idx}`} />
              </label>
              <label className="text-[11px] text-slate-500">Enthaltung
                <Input type="number" min="0" className="h-7 text-xs" value={it.votes_abstain || 0} onChange={(e) => upd(idx, "votes_abstain", Number(e.target.value))} disabled={!isEditable} />
              </label>
              <div className="text-xs text-right">
                {outcome && <Badge className={`text-[10px] ${outcome.color}`}>{outcome.label}</Badge>}
                {it.resolution_id && <Badge variant="outline" className="ml-1 text-[10px]">Beschluss</Badge>}
              </div>
            </div>
          </div>
        );
      })}
      {items.length === 0 && <div className="text-sm text-slate-500 text-center py-4">Keine TOPs.</div>}
    </div>
  );
}

function AttendanceTab({ a, isEditable, setAttendance }) {
  const owners = a.owners || [];
  const proxyMap = a.owner_proxies || {};
  const attMap = useMemo(() => {
    const m = {};
    (a.attendance || []).forEach((x) => m[x.contact_id] = x);
    return m;
  }, [a.attendance]);
  const setStatus = (cid, status) => {
    const next = (a.attendance || []).filter((x) => x.contact_id !== cid);
    next.push({ contact_id: cid, status, represented_by_contact_id: status === "represented" ? (attMap[cid]?.represented_by_contact_id || null) : null, proxy_note: attMap[cid]?.proxy_note || null });
    setAttendance(next);
  };
  const setProxy = (cid, rep_cid) => {
    const next = (a.attendance || []).filter((x) => x.contact_id !== cid);
    next.push({ contact_id: cid, status: "represented", represented_by_contact_id: rep_cid, proxy_note: attMap[cid]?.proxy_note || null });
    setAttendance(next);
  };

  // 2026-08 (iter111) — Live-Präsenz-Analyse: welche abwesenden Eigentümer haben
  // eine Vollmacht (→ Stimmen können übernommen werden) und welche nicht (→ Stimmen
  // gehen verloren)? Default-Status ist "absent" (analog zur Tabellenzeile),
  // damit Eigentümer ohne Attendance-Record im Banner mitgezählt werden.
  const _isAbsent = (o) => (attMap[o.id]?.status || "absent") === "absent";
  const absentWithProxy = owners.filter((o) => _isAbsent(o) && proxyMap[o.id]);
  const absentWithoutProxy = owners.filter((o) => _isAbsent(o) && !proxyMap[o.id]);
  const [filter, setFilter] = useState("all"); // all | absent-no-proxy

  const filteredOwners = filter === "absent-no-proxy"
    ? owners.filter((o) => _isAbsent(o) && !proxyMap[o.id])
    : owners;

  return (
    <div className="space-y-2" data-testid="attendance-tab">
      {/* 2026-08 (iter111) — Kompakter Warn-/Info-Banner */}
      {(absentWithoutProxy.length > 0 || absentWithProxy.length > 0) && (
        <div className="flex items-center gap-3 flex-wrap text-xs">
          {absentWithoutProxy.length > 0 && (
            <div className="inline-flex items-center gap-1.5 bg-rose-50 border border-rose-200 text-rose-900 rounded-md px-2.5 py-1" data-testid="attendance-warn-no-proxy">
              <Warning size={13} weight="fill" className="text-rose-600" />
              <b>{absentWithoutProxy.length}</b> abwesend ohne Vollmacht — Stimmen gehen verloren
            </div>
          )}
          {absentWithProxy.length > 0 && (
            <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md px-2.5 py-1" data-testid="attendance-info-with-proxy">
              <IdentificationCard size={13} className="text-emerald-700" />
              <b>{absentWithProxy.length}</b> abwesend mit Vollmacht
            </div>
          )}
          <Button
            size="sm" variant={filter === "absent-no-proxy" ? "default" : "outline"}
            className={`h-7 text-[11px] ${filter === "absent-no-proxy" ? "bg-rose-700 hover:bg-rose-800 text-white" : ""}`}
            onClick={() => setFilter(filter === "absent-no-proxy" ? "all" : "absent-no-proxy")}
            data-testid="attendance-filter-no-proxy"
          >
            {filter === "absent-no-proxy" ? "Alle anzeigen" : "Nur „ohne Vollmacht"}
          </Button>
        </div>
      )}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-3 py-2">Eigentümer</th>
            <th className="text-center px-3 py-2 w-24">MEA</th>
            <th className="text-center px-3 py-2 w-40">Status</th>
            <th className="text-left px-3 py-2">Vertretung durch</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filteredOwners.map((o) => {
            const att = attMap[o.id] || { status: "absent" };
            const proxy = proxyMap[o.id];
            const isAbsentNoProxy = att.status === "absent" && !proxy;
            return (
              <tr key={o.id} data-testid={`attendance-row-${o.id}`} className={isAbsentNoProxy ? "bg-rose-50/40" : ""}>
                <td className="px-3 py-2">
                  <div>{contactName(o)} <span className="text-xs text-slate-500">· {o.units?.length || 0} Einheit(en)</span></div>
                  {/* 2026-08 (iter111) — Vollmacht-Badge */}
                  {proxy && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid={`attendance-proxy-badge-${o.id}`}>
                      <IdentificationCard size={10} />
                      Vollmacht liegt vor{proxy.source === "seg" ? " · SEG" : " · extern"}
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${proxy.document_id}/download`}
                        target="_blank" rel="noreferrer"
                        className="underline hover:text-emerald-900"
                        title="Vollmacht öffnen"
                      >PDF</a>
                    </div>
                  )}
                  {isAbsentNoProxy && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-rose-700" data-testid={`attendance-no-proxy-${o.id}`}>
                      <Warning size={10} weight="fill" /> keine Vollmacht — Stimmen entfallen
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-center font-mono text-xs">{o.mea_sum || 0}</td>
                <td className="px-3 py-2">
                  <Select value={att.status} onValueChange={(v) => setStatus(o.id, v)} disabled={!isEditable}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`attendance-status-${o.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ATTENDANCE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2">
                  {att.status === "represented" && (
                    <Select value={att.represented_by_contact_id || "__none__"} onValueChange={(v) => setProxy(o.id, v === "__none__" ? null : v)} disabled={!isEditable}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Vertreter wählen" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— wählen —</SelectItem>
                        {owners.filter((oo) => oo.id !== o.id).map((oo) => <SelectItem key={oo.id} value={oo.id}>{contactName(oo)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </td>
              </tr>
            );
          })}
          {owners.length === 0 && (
            <tr><td colSpan={4} className="text-center py-4 text-slate-500 text-sm">Keine Eigentümer zugeordnet. Bitte Einheiten mit `owner_contact_id` versehen.</td></tr>
          )}
          {owners.length > 0 && filteredOwners.length === 0 && (
            <tr><td colSpan={4} className="text-center py-4 text-slate-500 text-sm">Kein Eigentümer entspricht dem Filter „abwesend ohne Vollmacht&quot;.</td></tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function MinutesTab({ a, isEditable, patch }) {
  return (
    <div>
      <Textarea
        rows={16}
        value={a.minutes || ""}
        onChange={(e) => patch({ minutes: e.target.value })}
        disabled={!isEditable}
        placeholder="Protokoll der Eigentümerversammlung …"
        data-testid="assembly-minutes"
      />
      <p className="text-xs text-slate-500 mt-2">
        Beim Abschließen der Versammlung werden alle mit {'"'}Angenommen{'"'} bewerteten TOPs automatisch als Beschlüsse in die Beschluss-Sammlung übernommen.
      </p>
    </div>
  );
}

// 2026-08 — Vollmachten-Tab: aus SEG-Analysen + externe Uploads für diese Versammlung
function ProxiesTab({ assemblyId }) {
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [grantorName, setGrantorName] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/weg/assemblies/${assemblyId}/proxies`)
      .then(({ data }) => setProxies(data || []))
      .catch(() => setProxies([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [assemblyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(
        `/weg/assemblies/${assemblyId}/proxies/upload`,
        fd,
        {
          params: grantorName ? { grantor_name: grantorName } : {},
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      toast.success("Externe Vollmacht archiviert");
      setGrantorName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="proxies-tab">
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-900">
        <b>Vollmachten-Sammlung</b> — alle für diese Versammlung erteilten oder eingegangenen Vollmachten (aus SEG-Analysen + extern hochgeladene).
        Rechtsgrundlage: § 25 Abs. 3 WEG.
      </div>
      {/* Upload externer Vollmacht */}
      <div className="bg-white border border-slate-200 rounded-md p-3 flex items-end gap-2">
        <div className="flex-1">
          <label className="text-xs text-slate-600 block mb-1">Vollmachtgeber (optional)</label>
          <Input value={grantorName} onChange={(e) => setGrantorName(e.target.value)}
                 placeholder="z. B. Familie Meier" data-testid="proxy-grantor-name" />
        </div>
        <label className="cursor-pointer">
          <input type="file" accept="application/pdf,image/*" className="hidden"
                 onChange={(e) => upload(e.target.files?.[0])} disabled={uploading}
                 data-testid="proxy-upload-input" />
          <span className="text-xs px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1.5">
            <FileArrowUp size={13} /> {uploading ? "Lade hoch…" : "Externe Vollmacht hochladen"}
          </span>
        </label>
      </div>
      {loading ? (
        <div className="text-sm text-slate-500 p-6 text-center">Lade …</div>
      ) : proxies.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-500 text-sm">
          <IdentificationCard size={28} className="mx-auto mb-2 text-slate-300" />
          Für diese Versammlung liegen noch keine Vollmachten vor.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Vollmachtgeber</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Quelle</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">WEG-Referenz</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Erstellt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {proxies.map((p, i) => (
                <tr key={p.document_id || i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`proxy-row-${p.document_id}`}>
                  <td className="px-3 py-2">{p.grantor_name}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${p.source === "seg" ? "bg-purple-100 text-purple-800 border-purple-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {p.source === "seg" ? "SEG-Analyse" : "extern"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{p.weg_property_ref || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString("de-DE") : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${p.document_id}/download`}
                       target="_blank" rel="noreferrer">
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"><Download size={11} /> PDF</Button>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

